import { Dropdown, FormItem } from '@getsynapse/design-system';
import get from 'lodash/get';
import React, { useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBudgetSettings,
  updateBudgetSettings,
} from 'state/BudgetList/budgetListSlice';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import {
  BudgetSettings,
  UpdateBudgetSettingsRequest,
} from 'utils/constants/budget';
import {
  selectOrganizationSettings,
  updateOrganizationSettings,
} from '../../../../state/Organization/organizationSlice';
import { CURRENCIES, MONTHS, TIME_FRAMES } from '../../../../utils/constants';
import {
  Option,
  OrganizationSettings,
  TimeFrame,
} from '../../../../utils/customTypes';

const FinancialSettings: React.FC = () => {
  const dispatch = useDispatch();

  const organizationSettings = useSelector(selectOrganizationSettings);
  const [currentSettings, setCurrentSettings] = useState<OrganizationSettings>(
    {}
  );

  const budgetSettings = useSelector(selectBudgetSettings);
  const [newBudgetSettings, setNewBudgetSettings] = useState<BudgetSettings>({
    fiscalYearStartMonth: 0,
    fiscalYearStartDay: 1,
    timeFrame: TimeFrame.MONTHLY,
    organizationId: '',
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    updatedBy: '',
    id: '',
  });

  useEffect(() => {
    setCurrentSettings(organizationSettings);
  }, [organizationSettings]);

  useEffect(() => {
    if (budgetSettings) {
      setNewBudgetSettings(budgetSettings);
    }
  }, [budgetSettings]);

  const handleChange = (
    property: keyof OrganizationSettings,
    value: boolean | string
  ) => {
    setCurrentSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, [property]: value };

      setTimeout(() => {
        dispatch(updateOrganizationSettings(updatedSettings));
      }, 1000);

      dispatch(
        showNotificationBanner({
          notificationVariant: 'success',
          notificationText: intl.get('GENERAL_SETTINGS.SUCCESS_MESSAGE'),
        })
      );

      return updatedSettings;
    });
  };

  const handleBudgetSettingsChange = (
    property: keyof BudgetSettings,
    value: string | number
  ) => {
    setNewBudgetSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings, [property]: value };

      const updatedBudgetSettingsReq: UpdateBudgetSettingsRequest = {
        month: updatedSettings.fiscalYearStartMonth,
        day: updatedSettings.fiscalYearStartDay,
        timeFrame: updatedSettings.timeFrame,
      };

      setTimeout(() => {
        dispatch(updateBudgetSettings(updatedBudgetSettingsReq));
      }, 1000);

      dispatch(
        showNotificationBanner({
          notificationVariant: 'success',
          notificationText: intl.get('FINANCIAL_PAGE.SUCCESS_MESSAGE'),
        })
      );

      return updatedSettings;
    });
  };

  const currentCurrency = get(
    currentSettings,

    'platformCurrency',
    intl.get('CURRENCY')
  );

  const currencyOptions = useMemo(
    () =>
      CURRENCIES.map((cur) => ({
        value: cur,
        label: intl.get(`CURRENCY_OPTIONS.${cur}`),
      })),
    []
  );

  const timeFrameOptions = useMemo(
    () =>
      TIME_FRAMES.map((tf) => ({
        value: tf.toLowerCase(),
        label: intl.get(
          `SETTINGS_PAGE.FINANCIAL_PAGE.TIME_FRAME_OPTIONS.${tf}`
        ),
      })),
    []
  );

  const monthOptions = useMemo(
    () =>
      MONTHS.map((month, index) => ({
        value: index,
        label: intl.get(
          `SETTINGS_PAGE.FINANCIAL_PAGE.FISCAL_YEAR_OPTIONS.${month}`
        ),
      })),

    []
  );

  return (
    <div className='w-full bg-white rounded-lg shadow-md'>
      <div className='py-2 text-neutral text-sm'>
        {intl.get('FINANCIAL_PAGE.DESCRIPTION')}
      </div>
      <div className='space-y-6 px-4 py-6'>
        {/* currency */}
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.FINANCIAL_PAGE.PLATFORM_CURRENCY_TITLE'
          )}
          labelProps={{
            required: true,
          }}
          className='flex flex-col w-1/3'
        >
          <Dropdown
            options={currencyOptions}
            placeholder={
              intl.get('SETTINGS_PAGE.FINANCIAL_PAGE.PLATFORM_CURRENCY') ||
              'Select Currency'
            }
            values={[
              {
                label: intl.get(`CURRENCY_OPTIONS.${currentCurrency}`),
                value: currentCurrency,
              },
            ]}
            onChange={(option: Option) => {
              handleChange('platformCurrency', option.value);
            }}
            className='w-full'
          />
        </FormItem>

        {/* fiscal year */}
        <FormItem
          label={intl.get('SETTINGS_PAGE.FINANCIAL_PAGE.FISCAL_YEAR_TITLE')}
          className='flex flex-col w-1/3'
        >
          <Dropdown
            options={monthOptions}
            values={
              newBudgetSettings
                ? [
                    monthOptions.find(
                      (option) =>
                        option.value === newBudgetSettings.fiscalYearStartMonth
                    ) || monthOptions[0],
                  ]
                : []
            }
            onChange={(option: Option) => {
              handleBudgetSettingsChange('fiscalYearStartMonth', option.value);
            }}
            className='w-full'
          />
        </FormItem>

        {/* budget timeframe */}
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.FINANCIAL_PAGE.BUDGETING_TIMEFRAME_TITLE'
          )}
          className='flex flex-col w-1/3'
        >
          <Dropdown
            options={timeFrameOptions}
            values={
              newBudgetSettings
                ? [
                    timeFrameOptions.find(
                      (option) => option.value === newBudgetSettings.timeFrame
                    ) || timeFrameOptions[0],
                  ]
                : []
            }
            onChange={(option: Option) => {
              handleBudgetSettingsChange('timeFrame', option.value);
            }}
            className='w-full'
          />
        </FormItem>
      </div>
    </div>
  );
};

export default FinancialSettings;
